import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from '../../../../UserContext';
import { 
    UserPlus, Send, X, Check, Clock, Trash2, UserX, 
    Search, Users, AtSign, ChevronRight, Target, Calendar, QrCode,
    TrendingUp
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Toast, ToastContainer } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import defaultProfile from '../../../../assets/images/profile.png';
import './Friends.css';
import { appConfig } from '../../../config/appConfig';
import { friendsService, profileService } from '../../../../services';
import StateCard from '../../../components/feedback/StateCard';
import MemberPassScannerModal from '../../../../components/memberPass/MemberPassScannerModal';

const USERNAME_PATTERN = /^[a-z](?:[a-z0-9._-]{1,28}[a-z0-9])?$/;

const Friends = () => {
    const location = useLocation();
    const [friends, setFriends] = useState([]);
    const [incomingFriendRequests, setIncomingFriendRequests] = useState([]);
    const [outgoingFriendRequests, setOutgoingFriendRequests] = useState([]);
    const [newFriendIdentifier, setNewFriendIdentifier] = useState('');
    const [memberPassToken, setMemberPassToken] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [showToast, setShowToast] = useState(false);
    const [loading, setLoading] = useState(true);
    const [addingFriend, setAddingFriend] = useState(false);
    const [showMemberPassScanner, setShowMemberPassScanner] = useState(false);
    const [memberPass, setMemberPass] = useState(null);
    const [highlightPendingRequests, setHighlightPendingRequests] = useState(false);
    const [memberPassCopied, setMemberPassCopied] = useState(false);
    const { user } = useUser();
    const userId = useMemo(() => user?.userId || user?.id, [user]);

    const triggerToast = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const fetchFriends = useCallback(async () => {
        try {
            const data = await friendsService.getFriends(userId);
            setFriends(Array.isArray(data) ? data : []);
        } catch {
            triggerToast('Failed to load friends.', 'error');
        }
    }, [userId]);

    const fetchFriendRequests = useCallback(async () => {
        try {
            const data = await friendsService.getFriendRequests(userId);
            setIncomingFriendRequests(data.incomingRequests || []);
            setOutgoingFriendRequests(data.outgoingRequests || []);
        } catch {
            triggerToast('Failed to load friend requests.', 'error');
        }
    }, [userId]);

    useEffect(() => {
        const load = async () => {
            if (!userId) return;
            setLoading(true);
            await Promise.all([fetchFriends(), fetchFriendRequests()]);
            setLoading(false);
        };
        load();
    }, [userId, fetchFriends, fetchFriendRequests]);

    useEffect(() => {
        const loadMemberPass = async () => {
            if (!userId) {
                setMemberPass(null);
                return;
            }

            try {
                const pass = await profileService.getMemberPass();
                setMemberPass(pass || null);
            } catch {
                setMemberPass(null);
            }
        };

        loadMemberPass();
    }, [userId]);

    useEffect(() => {
        const search = new URLSearchParams(location.search);
        const focusTarget = search.get('focus');
        const shouldFocusPending = focusTarget === 'pending-requests' || location.hash === '#pending-requests';
        const shouldFocusFriends = focusTarget === 'friends-list' || location.hash === '#friends-list';

        if (shouldFocusPending) {
            setHighlightPendingRequests(true);
            requestAnimationFrame(() => {
                document.getElementById('pending-requests')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            window.setTimeout(() => setHighlightPendingRequests(false), 2800);
            return;
        }

        if (shouldFocusFriends) {
            requestAnimationFrame(() => {
                document.getElementById('friends-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }
    }, [location.search, location.hash]);

    const addFriend = async (e) => {
        e.preventDefault();
        if (!newFriendIdentifier.trim()) return;
        setAddingFriend(true);
        try {
            const data = await friendsService.addFriend(userId, newFriendIdentifier.trim());
            triggerToast(data.message, 'success');
            setNewFriendIdentifier('');
            await fetchFriendRequests();
        } catch (error) {
            triggerToast(error.message || 'Network error. Try again.', 'error');
        } finally {
            setAddingFriend(false);
        }
    };

    const addFriendByPass = async (e) => {
        e.preventDefault();
        if (!memberPassToken.trim()) return;
        setAddingFriend(true);
        try {
            const data = await friendsService.addFriendByMemberPass(userId, memberPassToken.trim());
            triggerToast(data.message, 'success');
            setMemberPassToken('');
            await fetchFriendRequests();
        } catch (error) {
            triggerToast(error.message || 'Failed to add friend via member pass.', 'error');
        } finally {
            setAddingFriend(false);
        }
    };

    const handleMemberPassDetected = (value) => {
        setMemberPassToken(value);
        setShowMemberPassScanner(false);
        triggerToast('Member pass scanned. Submit to send request.');
    };

    const handleFriendAction = async (type, friendId) => {
        const endpoints = {
            accept: friendsService.acceptFriend,
            reject: friendsService.rejectFriend,
            remove: friendsService.removeFriend,
            cancel: friendsService.cancelFriend,
        };
        try {
            await endpoints[type](friendId);
            triggerToast(`Friend ${type}ed successfully!`);
            await Promise.all([fetchFriends(), fetchFriendRequests()]);
        } catch {
            triggerToast(`Failed to ${type} friend.`, 'error');
        }
    };

    const handleCopyMemberPass = useCallback(async () => {
        if (!memberPass?.qrValue) {
            return;
        }

        await navigator.clipboard.writeText(memberPass.qrValue);
        setMemberPassCopied(true);
        window.setTimeout(() => setMemberPassCopied(false), 2200);
    }, [memberPass]);

    const filteredFriends = useMemo(() => {
        if (!searchTerm.trim()) return friends;
        const term = searchTerm.toLowerCase();
        return friends.filter(f => 
            (f.friendName || '').toLowerCase().includes(term)
        );
    }, [friends, searchTerm]);

    const renderRequestCard = (request, type) => {
        const name = type === 'incoming' ? request.requesterName : request.receiverName;
        const image = request.imageUrl || defaultProfile;

        return (
            <div key={request.id} className="friends-request-card">
                <img src={image} alt={name} className="friends-request-avatar" />
                <div className="friends-request-info">
                    <h4>{name}</h4>
                    <span className={`friends-request-status ${type}`}>
                        {type === 'incoming' ? (
                            <><Clock size={12} /> Wants to connect</>
                        ) : (
                            <><Send size={12} /> Request sent</>
                        )}
                    </span>
                </div>
                <div className="friends-request-actions">
                    {type === 'incoming' ? (
                        <>
                            <button 
                                onClick={() => handleFriendAction('accept', request.id)}
                                className="friends-request-btn accept"
                                title="Accept"
                            >
                                <Check size={18} />
                            </button>
                            <button 
                                onClick={() => handleFriendAction('reject', request.id)}
                                className="friends-request-btn decline"
                                title="Decline"
                            >
                                <X size={18} />
                            </button>
                        </>
                    ) : (
                        <button 
                            onClick={() => handleFriendAction('cancel', request.id)}
                            className="friends-request-btn cancel"
                            title="Cancel Request"
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderFriendCard = (friend) => {
        const name = friend.friendName;
        const image = friend.imageUrl || defaultProfile;
        const canViewProfile = friend.publicProfileVisibility === 'public' || friend.publicProfileVisibility === 'friends';
        const profileRoute = USERNAME_PATTERN.test(friend.friendUserName || '')
            ? `/dashboard/friends/${encodeURIComponent(friend.friendUserName)}`
            : null;

        return (
            <div key={friend.id} className="friends-friend-card">
                <div className="friends-friend-header">
                    <img src={image} alt={name} className="friends-friend-avatar" />
                    <div className="friends-friend-info">
                        <h4>{name}</h4>
                        {canViewProfile && profileRoute ? (
                            <Link to={profileRoute} className="friends-view-profile">
                                View Profile <ChevronRight size={14} />
                            </Link>
                        ) : (
                            <span className="friends-private-profile">Private Profile</span>
                        )}
                    </div>
                </div>

                {(friend.lastShotWith || friend.lastEventDate || friend.accuracyPercentage) && (
                    <div className="friends-friend-stats">
                        {friend.lastShotWith && (
                            <div className="friends-stat-item">
                                <Target size={14} />
                                <span>{friend.lastShotWith}</span>
                            </div>
                        )}
                        {friend.lastEventDate && (
                            <div className="friends-stat-item">
                                <Calendar size={14} />
                                <span>{friend.lastEventDate}</span>
                            </div>
                        )}
                        {friend.accuracyPercentage && (
                            <div className="friends-stat-item highlight">
                                <TrendingUp size={14} />
                                <span>{friend.accuracyPercentage}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="friends-friend-footer">
                    <button 
                        onClick={() => handleFriendAction('remove', friend.id)}
                        className="friends-remove-btn"
                    >
                        <Trash2 size={14} />
                        Remove
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="friends-page">
            {/* Toast */}
            <ToastContainer className="friends-toast-container" position="top-end">
                <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
                    <Toast.Header>
                        <strong className="me-auto">{appConfig.appName}</strong>
                    </Toast.Header>
                    <Toast.Body className={toastType === 'error' ? 'text-danger' : 'text-success'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            {/* Header */}
            <div className="friends-header">
                <div className="friends-header-content">
                    <h1>Friends</h1>
                    <p>{appConfig.messaging.friendsTagline}</p>
                </div>
                <div className="friends-header-stats">
                    <div className="friends-header-stat">
                        <span className="friends-header-stat-value">{friends.length}</span>
                        <span className="friends-header-stat-label">Friends</span>
                    </div>
                    <div className="friends-header-stat">
                        <span className="friends-header-stat-value">{incomingFriendRequests.length + outgoingFriendRequests.length}</span>
                        <span className="friends-header-stat-label">Pending</span>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="friends-main-grid">
                {/* Left Column - Add Friend & Requests */}
                <div className="friends-sidebar">
                    {/* Add Friend Card */}
                    <div className="friends-add-card">
                        <div className="friends-add-header">
                            <div className="friends-add-icon">
                                <UserPlus size={20} />
                            </div>
                            <div>
                                <h3>Add Friend</h3>
                                <p>Send a friend request</p>
                            </div>
                        </div>
                        <form onSubmit={addFriend} className="friends-add-form">
                            <div className="friends-add-input">
                                <AtSign size={18} className="friends-input-icon" />
                                <input
                                    type="text"
                                    value={newFriendIdentifier}
                                    onChange={(e) => setNewFriendIdentifier(e.target.value)}
                                    placeholder="Email or username"
                                    required
                                />
                            </div>
                            <button type="submit" disabled={!newFriendIdentifier.trim() || addingFriend}>
                                {addingFriend ? 'Sending...' : 'Send Request'}
                            </button>
                        </form>
                        <form onSubmit={addFriendByPass} className="friends-add-form friends-pass-form">
                            <div className="friends-add-input">
                                <AtSign size={18} className="friends-input-icon" />
                                <input
                                    type="text"
                                    value={memberPassToken}
                                    onChange={(e) => setMemberPassToken(e.target.value)}
                                    placeholder="Paste Member Pass QR token"
                                    required
                                />
                            </div>
                            <div className="friends-pass-actions">
                                <button
                                    type="button"
                                    className="friends-scan-pass-btn"
                                    onClick={() => setShowMemberPassScanner(true)}
                                >
                                    Scan Member Pass QR
                                </button>
                            </div>
                            <button type="submit" disabled={!memberPassToken.trim() || addingFriend}>
                                {addingFriend ? 'Adding...' : 'Add via Member Pass'}
                            </button>
                        </form>
                        {memberPass?.qrValue && (
                            <div className="friends-connect-pass-card">
                                <div className="friends-connect-pass-header">
                                    <QrCode size={16} />
                                    <h4>Your Connect Pass QR</h4>
                                </div>
                                <p>Use this same pass for Add Friend and Connect Perk claims at venues.</p>
                                <div className="friends-connect-pass-qr">
                                    <QRCodeSVG value={memberPass.qrValue} size={132} includeMargin />
                                </div>
                                <button
                                    type="button"
                                    className="friends-connect-pass-copy"
                                    onClick={handleCopyMemberPass}
                                >
                                    {memberPassCopied ? 'Copied ✓' : 'Copy pass code'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Pending Requests Section */}
                    <div id="pending-requests" className={`friends-requests-card ${highlightPendingRequests ? 'friends-requests-card-highlight' : ''}`}>
                        <div className="friends-requests-header">
                            <h3>
                                <Clock size={18} />
                                Pending Requests
                            </h3>
                            {(incomingFriendRequests.length + outgoingFriendRequests.length) > 0 && (
                                <span className="friends-requests-badge">
                                    {incomingFriendRequests.length + outgoingFriendRequests.length}
                                </span>
                            )}
                        </div>

                        {loading ? (
                            <StateCard
                                icon={Clock}
                                title="Loading requests"
                                description="Checking incoming and outgoing friend requests."
                                compact
                                loading
                            />
                        ) : (
                            <>
                                {incomingFriendRequests.length > 0 && (
                                    <div className="friends-requests-section">
                                        <span className="friends-requests-label">Incoming</span>
                                        {incomingFriendRequests.map(req => renderRequestCard(req, 'incoming'))}
                                    </div>
                                )}

                                {outgoingFriendRequests.length > 0 && (
                                    <div className="friends-requests-section">
                                        <span className="friends-requests-label">Sent</span>
                                        {outgoingFriendRequests.map(req => renderRequestCard(req, 'outgoing'))}
                                    </div>
                                )}

                                {incomingFriendRequests.length === 0 && outgoingFriendRequests.length === 0 && (
                                    <StateCard
                                        icon={UserX}
                                        title="No pending requests"
                                        description="New requests you send or receive will show up here."
                                        compact
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Right Column - Friends List */}
                <div id="friends-list" className="friends-list-container">
                    <div className="friends-list-header">
                        <h2>
                            <Users size={22} />
                            Your Friends
                        </h2>
                        {friends.length > 0 && (
                            <div className="friends-search">
                                <Search size={18} className="friends-search-icon" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search friends..."
                                />
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <StateCard
                            icon={Users}
                            title="Loading your friends"
                            description="Pulling together your network and recent requests."
                            loading
                        />
                    ) : friends.length === 0 ? (
                        <StateCard
                            icon={Users}
                            title="No friends yet"
                            description={`Start connecting with fellow ${appConfig.terminology.communityPlural} by sending friend requests using the form on the left.`}
                        />
                    ) : filteredFriends.length === 0 ? (
                        <StateCard
                            icon={Search}
                            title="No friends match your search"
                            description={`We couldn't find anyone matching "${searchTerm}".`}
                            actionLabel="Clear Search"
                            actionOnClick={() => setSearchTerm('')}
                        />
                    ) : (
                        <div className="friends-grid">
                            {filteredFriends.map(friend => renderFriendCard(friend))}
                        </div>
                    )}
                </div>
            </div>
            <MemberPassScannerModal
                isOpen={showMemberPassScanner}
                onClose={() => setShowMemberPassScanner(false)}
                onDetected={handleMemberPassDetected}
                title="Scan Friend Member Pass"
                description="Scan their profile QR to fill the member pass token."
            />
        </div>
    );
};

export default Friends;
